// src/pages/MatrizForm.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Paper, Typography, TextField, Button, Box, FormControlLabel, Checkbox, MenuItem, Alert } from '@mui/material';
import api from '../api/axiosConfig';

const MatrizForm = () => {
  const { idPoa, idActividad } = useParams();
  const navigate = useNavigate();
  const user = localStorage.getItem('user');

  const [formData, setFormData] = useState({
    id_poa_proyecto: parseInt(idPoa, 10),
    id_institucion: '',
    descripcion: '',
    producto: '',
    cantidad: 1,
    cantidad2: 0,
    resultado: '',
    indicador: '',
    frecuencia: '000000000000',
  });

  const [instituciones, setInstituciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Cargar instituciones
  useEffect(() => {
    const fetchInstituciones = async () => {
      try {
        const res = await api.get('/instituciones/');
        setInstituciones(res.data);
      } catch (err) {
        console.error('Error al cargar instituciones:', err);
      }
    };
    fetchInstituciones();
  }, []);

  // Cargar datos si es edición
  useEffect(() => {
    if (idActividad) {
      const fetchActividad = async () => {
        setLoading(true);
        try {
          const res = await api.get(`/poa-actividades/${idActividad}/`);
          setFormData({
            ...res.data,
            frecuencia: res.data.frecuencia.padEnd(12, '0').substring(0, 12),
          });
        } catch (err) {
          setError('Error al cargar la actividad');
        } finally {
          setLoading(false);
        }
      };
      fetchActividad();
    }
  }, [idActividad]);

  const handleMesChange = (index) => {
    const newFreq = formData.frecuencia.split('');
    newFreq[index] = newFreq[index] === '1' ? '0' : '1';
    setFormData({ ...formData, frecuencia: newFreq.join('') });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const payload = {
      ...formData,
      creado_por: user,
      modificado_por: user,
    };

    try {
      if (idActividad) {
        await api.put(`/poa-actividades/${idActividad}/`, {
          ...formData,
          modificado_por: user,
        });
      } else {
        await api.post('/poa-actividades/', payload);
      }
      navigate(`/poa/${idPoa}/matriz`);
    } catch (err) {
      console.error('Error al guardar actividad:', err.response || err);
      setError(err.response?.data?.detail || 'Error al guardar. Verifica los datos.');
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" gutterBottom>
          {idActividad ? 'Editar Actividad' : 'Nueva Actividad'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            select
            label="Institución *"
            fullWidth
            value={formData.id_institucion}
            onChange={(e) => setFormData({ ...formData, id_institucion: e.target.value })}
            margin="normal"
            required
          >
            {instituciones.map(inst => (
              <MenuItem key={inst.id_institucion} value={inst.id_institucion}>
                {inst.nombre}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Descripción *"
            fullWidth
            multiline
            rows={2}
            value={formData.descripcion}
            onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
            margin="normal"
            required
          />

          <TextField
            label="Producto *"
            fullWidth
            multiline
            rows={2}
            value={formData.producto}
            onChange={(e) => setFormData({ ...formData, producto: e.target.value })}
            margin="normal"
            required
          />

          <TextField
            label="Cantidad"
            type="number"
            fullWidth
            value={formData.cantidad}
            onChange={(e) => setFormData({ ...formData, cantidad: Number(e.target.value) || 0 })}
            margin="normal"
          />

          <TextField
            label="Resultado *"
            fullWidth
            multiline
            rows={2}
            value={formData.resultado}
            onChange={(e) => setFormData({ ...formData, resultado: e.target.value })}
            margin="normal"
            required
          />

          <TextField
            label="Indicador *"
            fullWidth
            multiline
            rows={2}
            value={formData.indicador}
            onChange={(e) => setFormData({ ...formData, indicador: e.target.value })}
            margin="normal"
            required
          />

          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>Frecuencia (meses)</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Array.from({ length: 12 }, (_, i) => (
              <FormControlLabel
                key={i}
                control={
                  <Checkbox
                    checked={formData.frecuencia[i] === '1'}
                    onChange={() => handleMesChange(i)}
                  />
                }
                label={`${i + 1}`}
              />
            ))}
          </Box>

          <Box sx={{ mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ mr: 1 }}
            >
              {loading ? 'Guardando...' : idActividad ? 'Actualizar' : 'Guardar'}
            </Button>
            <Button onClick={() => navigate(`/poa/${idPoa}/matriz`)}>
              Cancelar
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default MatrizForm;